// src/utils/indietroChiudeFinestre.js
// ═══════════════════════════════════════════════════════════════════════════
// IL TASTO «INDIETRO» CHIUDE PRIMA LA FINESTRA APERTA (ottobre 2026, W400)
// ═══════════════════════════════════════════════════════════════════════════
// Con la W391 il tasto «indietro» del telefono (o del browser) torna alla schermata
// precedente. Ma se è aperta una finestra (un rapportino, una conferma, il menu sul
// telefono…) ci si aspetta che «indietro» chiuda la finestra, come in tutte le app, e non
// che porti via dalla pagina lasciandola aperta sopra un'altra.
// Ora, se c'è una finestra aperta:
//  1. la pagina resta dov'è (il passo di cronologia appena tolto si rimette);
//  2. la finestra si chiude nel modo in cui la chiuderebbe la persona: tasto Esc, tocco
//     sullo sfondo, oppure il pulsante di chiusura in alto («✕», «Chiudi») o «Annulla»;
//  3. se la finestra non si lascia chiudere così, resta aperta e non succede nulla:
//     meglio che portare via la persona da un modulo a metà.
// Le finestre sono quelle riconosciute da utils/finestreAperte.js. I moduli lunghi hanno
// comunque la bozza automatica (utils/bozzeLocali.js).
// ═══════════════════════════════════════════════════════════════════════════
import { SELETTORE_FINESTRE, finestreAperte } from "./finestreAperte";

const attesa = (ms) => new Promise(r => setTimeout(r, ms));

/** La finestra in primo piano: quella con lo z-index più alto (a parità, l'ultima). */
export function finestraInCima() {
  const tutte = finestreAperte();
  let migliore = null, zMax = -Infinity;
  tutte.forEach(el => {
    const z = parseInt(getComputedStyle(el).zIndex, 10);
    const zz = Number.isFinite(z) ? z : 0;
    if (zz >= zMax) { zMax = zz; migliore = el; }
  });
  return migliore;
}

const testo = (b) => (b.textContent || "").replace(/\s+/g, " ").trim();

/** Il pulsante con cui la persona chiuderebbe questa finestra, se c'è. */
export function pulsanteChiusura(el) {
  const pulsanti = [...el.querySelectorAll("button, [role='button']")].filter(b => !b.disabled && b.getClientRects().length);
  // la parte alta della finestra: lì sta la «✕» di chiusura (più in basso le «✕» servono
  // a togliere una riga del modulo e non vanno toccate)
  const pannello = (el.firstElementChild && el.firstElementChild.getClientRects().length ? el.firstElementChild : el).getBoundingClientRect();
  const inAlto = (b) => b.getBoundingClientRect().top - Math.max(0, pannello.top) < 90;
  const etichetta = (b) => `${b.getAttribute("aria-label") || ""} ${b.getAttribute("title") || ""}`;
  return (
    pulsanti.find(b => /chiudi|close/i.test(etichetta(b))) ||
    pulsanti.find(b => inAlto(b) && ["✕", "×", "✖", "X", "x"].includes(testo(b))) ||
    pulsanti.find(b => inAlto(b) && !testo(b) && b.querySelector("svg.lucide-x")) ||
    pulsanti.find(b => /^chiudi$/i.test(testo(b))) ||
    pulsanti.find(b => /^annulla$/i.test(testo(b))) ||
    null
  );
}

const ancoraAperta = (el) => el.isConnected && (el.matches(SELETTORE_FINESTRE) || finestreAperte().includes(el));

/** Prova a chiudere la finestra; true se si è chiusa. */
export async function chiudiFinestra(el) {
  const tentativi = [
    () => (document.activeElement || document).dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", code: "Escape", bubbles: true })),
    () => el.dispatchEvent(new MouseEvent("click", { bubbles: true })),          // tocco sullo sfondo
    () => { const b = pulsanteChiusura(el); if (b) b.click(); },
  ];
  for (const prova of tentativi) {
    try { prova(); } catch { /* si passa al tentativo successivo */ }
    await attesa(80);
    if (!ancoraAperta(el)) return true;
  }
  return false;
}

export function installaIndietroChiudeFinestre() {
  if (typeof window === "undefined" || window.__nxIndietroFinestre) return;
  window.__nxIndietroFinestre = true;
  // l'ultima voce di cronologia in cui si trovava l'app (per rimetterla al suo posto)
  let ultima = { stato: window.history.state, url: window.location.href };
  const segna = () => { ultima = { stato: window.history.state, url: window.location.href }; };
  ["pushState", "replaceState"].forEach(nome => {
    const originale = window.history[nome].bind(window.history);
    window.history[nome] = (...a) => { const r = originale(...a); segna(); return r; };
  });
  // registrato prima degli ascoltatori dell'app: se c'è una finestra aperta, l'app non
  // riceve il «indietro» e non cambia pagina
  window.addEventListener("popstate", (e) => {
    const el = finestraInCima();
    if (!el) { segna(); return; }
    e.stopImmediatePropagation();
    try { window.history.pushState(ultima.stato, "", ultima.url); } catch { /* cronologia non disponibile */ }
    chiudiFinestra(el);
  }, true);
}
